import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Zap } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import api from "@/lib/api";
import ShortAnswer from "@/components/ShortAnswer";
import { AUTHOR } from "@/data/author";
import { SHORT_ANSWERS } from "@/data/short-answers";

const renderBold = (text, keyPrefix) =>
  text.split(/(\*\*[^*]+\*\*)/g).map((seg, j) => {
    const b = seg.match(/^\*\*([^*]+)\*\*$/);
    return b ? <strong key={`${keyPrefix}-${j}`} className="font-semibold text-ink">{b[1]}</strong> : seg;
  });

const renderInline = (text) =>
  text.split(/(\[[^\]]+\]\([^)]+\))/g).map((part, i) => {
    const m = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (!m) return renderBold(part, i);
    const [, label, href] = m;
    const cls = "font-semibold underline decoration-pink decoration-2 underline-offset-4 transition-colors hover:text-pink";
    if (/^https?:\/\//.test(href)) {
      return <a key={i} href={href} target="_blank" rel="noopener noreferrer" className={cls}>{label}</a>;
    }
    return <Link key={i} to={href} className={cls}>{label}</Link>;
  });

const renderBody = (content) => {
  const blocks = [];
  let list = [];
  let olist = [];
  let key = 0;
  const flushUl = () => {
    if (list.length) {
      const items = list;
      blocks.push(
        <ul key={`ul-${key++}`} className="my-5 space-y-2.5">
          {items.map((it, j) => (
            <li key={j} className="flex gap-3 text-base leading-relaxed text-ink/75">
              <span className="mt-2.5 h-1.5 w-1.5 shrink-0 bg-pink" aria-hidden="true" />
              <span>{renderInline(it)}</span>
            </li>
          ))}
        </ul>
      );
      list = [];
    }
  };
  const flushOl = () => {
    if (olist.length) {
      const items = olist;
      blocks.push(
        <ol key={`ol-${key++}`} className="my-5 space-y-2.5">
          {items.map((it, j) => (
            <li key={j} className="flex gap-3 text-base leading-relaxed text-ink/75">
              <span className="mt-0.5 shrink-0 font-mono text-sm font-semibold text-pink" aria-hidden="true">{j + 1}.</span>
              <span>{renderInline(it)}</span>
            </li>
          ))}
        </ol>
      );
      olist = [];
    }
  };
  const flush = () => { flushUl(); flushOl(); };
  content.split("\n").forEach((line) => {
    const t = line.trim();
    const num = t.match(/^\d+\.\s+(.*)$/);
    if (t.startsWith("## ")) {
      flush();
      blocks.push(
        <h2 key={`h-${key++}`} className="mt-10 font-display text-xl sm:text-2xl font-semibold tracking-tight">
          {t.slice(3)}
        </h2>
      );
    } else if (t.startsWith("- ")) {
      flushOl();
      list.push(t.slice(2));
    } else if (num) {
      flushUl();
      olist.push(num[0].replace(/^\d+\.\s+/, ""));
    } else if (t) {
      flush();
      blocks.push(<p key={`p-${key++}`} className="mt-5 text-base leading-relaxed text-ink/75">{renderInline(t)}</p>);
    }
  });
  flush();
  return blocks;
};

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [state, setState] = useState("loading");

  useEffect(() => {
    setState("loading");
    setRelated([]);
    api.get(`/blog/posts/${slug}`)
      .then((r) => {
        setPost(r.data);
        setState("ok");
        api.get("/blog/posts")
          .then((l) => {
            const all = l.data.posts || [];
            const wanted = r.data.related || [];
            let rel = wanted.map((s) => all.find((p) => p.slug === s)).filter(Boolean);
            if (!rel.length) rel = all.filter((p) => p.slug !== slug && p.category === r.data.category);
            if (!rel.length) rel = all.filter((p) => p.slug !== slug);
            setRelated(rel.slice(0, 3));
          })
          .catch(() => {});
      })
      .catch(() => setState("missing"));
  }, [slug]);

  const origin = window.location.origin;

  if (state === "loading") {
    return (
      <main className="bg-paper pt-40 pb-24">
        <div className="container-x max-w-3xl space-y-4">
          <div className="h-10 w-3/4 animate-pulse bg-ink/10" />
          <div className="h-4 w-full animate-pulse bg-ink/5" />
          <div className="h-4 w-5/6 animate-pulse bg-ink/5" />
        </div>
      </main>
    );
  }

  if (state === "missing") {
    return (
      <main data-testid="blog-post-missing" className="bg-paper pt-40 pb-24">
        <div className="container-x max-w-3xl">
          <h1 className="font-display text-3xl font-semibold">That briefing isn't here.</h1>
          <p className="mt-4 text-ink/60">It may have moved. The full list is one click away.</p>
          <Link to="/blog" data-testid="blog-missing-back" className="mt-6 inline-flex items-center gap-2 font-bold">
            <ArrowLeft size={17} /> All briefings
          </Link>
        </div>
      </main>
    );
  }

  const published = new Date(post.published_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  const modifiedValue = post.updated_at || post.published_at;
  // "Last reviewed" only appears when the article was genuinely revisited
  // after publication, never as a copy of the publish date.
  const dayOf = (iso) => new Date(iso).toISOString().slice(0, 10);
  const reviewedLater = Boolean(post.updated_at) && dayOf(post.updated_at) > dayOf(post.published_at);
  const modified = new Date(modifiedValue).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  const postPath = `/blog/${post.slug}`;
  const hasShortAnswer = Boolean(SHORT_ANSWERS[postPath]);
  const faqs = post.faqs || [];

  // HowTo steps pulled from the article's own step section (numbered or bulleted)
  const steps = [];
  let inSteps = false;
  post.content.split("\n").forEach((raw) => {
    const t = raw.trim();
    if (t.startsWith("## ")) { inSteps = /step/i.test(t); return; }
    if (!inSteps) return;
    const m = t.match(/^\d+\.\s+(.*)$/) || t.match(/^-\s+(.*)$/);
    if (m) steps.push(m[1]);
  });
  const howTo = steps.length >= 3
    ? [{
        "@type": "HowTo",
        name: post.title,
        step: steps.map((s) => {
          const parts = s.split(" - ");
          if (parts.length > 1) return { "@type": "HowToStep", name: parts[0].trim(), text: parts.slice(1).join(" - ").trim() };
          return { "@type": "HowToStep", name: s.split(" ").slice(0, 8).join(" "), text: s };
        }),
      }]
    : [];
  const faqSchema = faqs.length
    ? [{
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }]
    : [];

  return (
    <main data-testid={`blog-post-${post.slug}`}>
      <Seo
        title={`${post.seo_title || post.title} | Electra HR`}
        siteName="Electra HR"
        description={post.excerpt}
        type="article"
        image={`/api/og/blog-${post.slug}.jpg`}
        publishedTime={post.published_at}
        modifiedTime={modifiedValue}
        authorName={AUTHOR.name}
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Article",
              headline: post.title,
              description: post.excerpt,
              author: {
                "@type": "Person",
                name: AUTHOR.name,
                jobTitle: AUTHOR.jobTitle,
                description: AUTHOR.credential,
                url: `${origin}${AUTHOR.path}`,
              },
              publisher: { "@id": `${origin}/#organization` },
              datePublished: post.published_at,
              dateModified: modifiedValue,
              mainEntityOfPage: { "@type": "WebPage", "@id": `${origin}/blog/${post.slug}` },
              image: `${origin}/api/og/blog-${post.slug}.jpg`,
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: origin },
                { "@type": "ListItem", position: 2, name: "Briefings", item: `${origin}/blog` },
                { "@type": "ListItem", position: 3, name: post.title, item: `${origin}/blog/${post.slug}` },
              ],
            },
            ...howTo,
            ...faqSchema,
          ],
        }}
      />
      <article>
        <header className="grain bg-ink pb-14 pt-32 sm:pt-40 text-paper">
          <div className="container-x max-w-4xl">
            <Link to="/blog" data-testid="blog-post-back" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-paper/50 hover:text-pink transition-colors">
              <ArrowLeft size={14} /> Briefings
            </Link>
            <p className="eyebrow mt-8 text-pink">{post.category}</p>
            <h1 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.08]">
              {post.title}
            </h1>
            <p data-testid="blog-byline" className="mt-6 text-sm leading-relaxed text-paper/75">
              Written by{" "}
              <Link to={AUTHOR.path} rel="author" className="font-semibold text-paper underline decoration-pink decoration-2 underline-offset-4 hover:text-pink">
                {AUTHOR.name}
              </Link>
              , {AUTHOR.credential}
            </p>
            <p className="mt-2 font-mono text-xs uppercase tracking-[0.18em] text-paper/45">
              Published <time dateTime={post.published_at}>{published}</time>
              {reviewedLater && <> · Last reviewed <time dateTime={post.updated_at}>{modified}</time></>}
              {" "}· {post.read_time} min read
            </p>
            {post.reviewed_by && (
              <p data-testid="blog-reviewed-by" className="mt-3 text-xs leading-relaxed text-paper/55">
                {post.reviewed_by}
              </p>
            )}
          </div>
        </header>
        <div className="bg-paper py-14 sm:py-20">
          <div className="container-x max-w-3xl">
            {hasShortAnswer ? (
              <ShortAnswer path={postPath} />
            ) : (
              <Reveal>
                <div data-testid="blog-takeaway-box" className="flex gap-4 border-l-4 border-pink bg-pink/10 p-6">
                  <Zap size={20} className="mt-0.5 shrink-0 text-ink" />
                  <p className="text-base font-medium leading-relaxed text-ink/80">{post.excerpt}</p>
                </div>
              </Reveal>
            )}
            <div data-testid="blog-post-body" className="mt-4">{renderBody(post.content)}</div>
            {faqs.length > 0 && (
              <section data-testid="blog-post-faqs" className="mt-14">
                <h2 className="font-display text-2xl font-semibold tracking-tight">Questions employers ask</h2>
                <div className="mt-5 divide-y divide-ink/10 border-y border-ink/10">
                  {faqs.map((f, i) => (
                    <details key={f.q} className="group py-5" data-testid={`blog-faq-${i}`}>
                      <summary className="cursor-pointer list-none font-display text-lg font-semibold marker:hidden">{f.q}</summary>
                      <p className="mt-3 text-sm sm:text-base leading-relaxed text-ink/70">{f.a}</p>
                    </details>
                  ))}
                </div>
              </section>
            )}
            <div className="mt-14 border-2 border-ink bg-ink p-8 text-paper">
              <p className="font-display text-xl font-semibold">Dealing with this right now?</p>
              <p className="mt-2 text-sm leading-relaxed text-paper/65">
                Reading about it is step one. A same-day answer is step two, and it's the easier one.
              </p>
              <Link to="/contact" data-testid="blog-post-cta" className="group mt-5 inline-flex items-center gap-2 bg-pink px-6 py-3 font-bold text-ink transition-transform duration-300 hover:-translate-y-0.5">
                Talk to us <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
            {related.length > 0 && (
              <div data-testid="related-posts" className="mt-14">
                <p className="eyebrow text-ink/50">Related reading</p>
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-5">
                  {related.map((r) => (
                    <Link
                      key={r.slug}
                      to={`/blog/${r.slug}`}
                      data-testid={`related-card-${r.slug}`}
                      className="group flex h-full flex-col border-2 border-ink p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-[6px_6px_0_0_#0F0F1A]"
                    >
                      <p className="font-mono text-xs uppercase tracking-[0.15em] text-ink/50">{r.category}</p>
                      <h3 className="mt-3 font-display text-base font-semibold leading-snug">{r.title}</h3>
                      <span className="mt-auto pt-4 flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-ink/45 group-hover:text-ink">
                        Read <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
            <Reveal>
              <div data-testid="author-bio-card" className="mt-14 flex flex-col sm:flex-row gap-6 border-2 border-ink p-7" itemScope itemType="https://schema.org/Person">
                <img
                  src="/images/vanessa-challess-480.webp"
                  alt={`${AUTHOR.name}, founder of Electra HR`}
                  loading="lazy"
                  className="h-28 w-28 shrink-0 object-cover object-top"
                  itemProp="image"
                />
                <div>
                  <p className="eyebrow text-ink/50">About the author</p>
                  <p className="mt-2 font-display text-lg font-semibold" itemProp="name">{AUTHOR.name}</p>
                  <p className="mt-1 text-sm leading-relaxed text-ink/70" itemProp="description">
                    Practising litigation solicitor and founder of Electra HR, the one who got tired
                    of watching founders pay law-firm rates for HR answers.
                  </p>
                  <Link to="/about" data-testid="author-bio-link" className="group mt-3 inline-flex items-center gap-2 text-sm font-bold">
                    More about Electra HR <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </article>
    </main>
  );
}
